import React, { useCallback, useEffect, useState } from 'react';
import { GeoJSON, MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { getManagedLayerPreview } from '../../services/geographyService';

export const PREVIEW_MIN_ZOOM = 11;
const PREVIEW_LIMIT = 3000;

function LocalFeatures({ layerId, version }) {
  const map = useMap();
  const [viewport, setViewport] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const updateViewport = useCallback(() => {
    if (map.getZoom() < PREVIEW_MIN_ZOOM) {
      setViewport(null);
      return;
    }
    const bounds = map.getBounds();
    const west = bounds.getWest();
    const east = bounds.getEast();
    // Leaflet permits panning into wrapped copies of the world.
    const offset = 360 * Math.floor((west + 180) / 360);
    const bbox = [
      Math.max(-180, west - offset), Math.max(-90, bounds.getSouth()),
      Math.min(180, east - offset), Math.min(90, bounds.getNorth()),
    ];
    setViewport(bbox[0] < bbox[2] && bbox[1] < bbox[3] ? bbox.join(',') : null);
  }, [map]);

  useMapEvents({
    movestart: () => { setViewport(null); setResult(null); },
    zoomstart: () => { setViewport(null); setResult(null); },
    moveend: updateViewport,
    zoomend: updateViewport,
  });

  useEffect(() => {
    if (version.bounds?.length === 4) {
      const [west, south, east, north] = version.bounds;
      map.fitBounds([[south, west], [north, east]], { padding: [20, 20] });
    }
    // Read the initial viewport as fitBounds can finish before events are attached.
    updateViewport();
  }, [map, version, updateViewport]);

  useEffect(() => {
    setResult(null);
    setError('');
    setLoading(false);
    if (!viewport) return undefined;
    let current = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await getManagedLayerPreview(layerId, version._id, {
          bbox: viewport, limit: PREVIEW_LIMIT,
        });
        if (current) setResult({ ...response.data, viewport });
      } catch (requestError) {
        if (current) setError(requestError.response?.data?.message || 'Could not load local shapes. Pan or zoom to try again.');
      } finally {
        if (current) setLoading(false);
      }
    }, 250);
    return () => { current = false; clearTimeout(timer); };
  }, [layerId, version._id, viewport]);

  const visible = result?.viewport === viewport ? result : null;
  return (
    <>
      <div className="leaflet-top leaflet-right" style={{ padding: 10, maxWidth: '75%', pointerEvents: 'none' }}>
        <div role="status" style={{ background: '#fff', padding: '8px 12px', borderRadius: 6, boxShadow: '0 1px 4px #888' }}>
          {!viewport ? `Zoom in to a local area (zoom ${PREVIEW_MIN_ZOOM}+) to see shapes.`
            : loading ? 'Loading shapes in this area...'
              : error || (visible
                ? visible.truncated
                  ? `Showing ${visible.features.length.toLocaleString()} of ${visible.totalParts.toLocaleString()} feature parts in this area. Zoom in further to see all.`
                  : `${visible.features.length.toLocaleString()} feature parts in this area.`
                : 'Preparing local shapes...')}
        </div>
      </div>
      {visible?.features?.length > 0 && <GeoJSON
        key={visible.viewport}
        data={visible}
        style={{ color: '#0369a1', weight: 2, fillColor: '#38bdf8', fillOpacity: 0.15 }}
        onEachFeature={(feature, layer) => {
          if (feature.properties?.name) layer.bindTooltip(String(feature.properties.name), { sticky: true });
        }}
      />}
    </>
  );
}

export default function GeographyPreviewMap({ layerId, version }) {
  return (
    <div className="geo-layers__preview-map">
      <MapContainer center={[50, -90]} zoom={5} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <LocalFeatures layerId={layerId} version={version} />
      </MapContainer>
    </div>
  );
}
